import request from './request'

export interface User {
  id: string
  username: string
  email: string
  avatar: string
  nickname: string
  bio: string
  role: string
  school: string
  ip_location?: string
  created_at: string
  updated_at: string
  // 统计信息
  solved_count: number      // 解题数（去重）
  submission_count: number  // 提交数（不去重）
  contest_count: number     // 参赛数
  accepted_count: number    // 通过数（从提交记录计算）
  submitted_count: number   // 提交总数（从提交记录计算）
  acceptance_rate: number   // 通过率
  ranking: number           // 排名
  total_score: number       // 总分数
  continuous_days: number   // 连续打卡天数
}

export interface DeviceInfo {
  browser: string
  browser_version: string
  os: string
  os_version: string
  device: string
  device_type: string
  platform: 'web' | 'app' | 'mini-program'
  cpu: string
  screen_resolution: string
  language: string
  timezone: string
}

export interface LoginRequest {
  username: string
  password: string
  device_info?: DeviceInfo
}

export interface Point {
  x: number
  y: number
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
  code: string
  captchaId: string
  points: Point[]
}

export interface RegisterResponse {
  id: string
  username: string
  email: string
}

/**
 * 发送邮箱验证码请求
 */
export interface SendEmailCodeRequest {
  email: string
  type: 'register' | 'reset'
}

/**
 * 发送邮箱验证码响应
 */
export interface SendEmailCodeResponse {
  expire: number
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: User;
  is_new_location?: boolean;
  last_location?: string;
}

// 登录历史
export interface LoginHistory {
  id: string;
  ip_address: string;
  ip_location: string;
  user_agent: string;
  device_type: string;
  browser: string;
  os: string;
  login_type: string;
  is_abnormal: boolean;
  abnormal_reason: string;
  created_at: string;
}

export interface GetLoginHistoryListRequest {
  page?: number;
  page_size?: number;
}

export interface GetLoginHistoryListResponse {
  list: LoginHistory[];
  total: number;
  page: number;
}

export interface ProfileResponse {
  user: User
}

/**
 * 用户登录
 */
export function login(data: LoginRequest): Promise<LoginResponse> {
  return request.post('/auth/login', data)
}

/**
 * 用户注册
 */
export function register(data: RegisterRequest): Promise<RegisterResponse> {
  return request.post('/auth/register', data)
}

/**
 * 发送邮箱验证码
 * @param data 请求参数
 */
export function sendEmailCode(data: SendEmailCodeRequest): Promise<SendEmailCodeResponse> {
  return request.post('/auth/email/code', data)
}

/**
 * 退出登录
 */
export function logout(): Promise<{ success: boolean }> {
  return request.post('/auth/logout', {})
}

/**
 * 获取当前用户信息
 */
export function getCurrentUser(): Promise<ProfileResponse> {
  return request.get('/user/profile')
}

/**
 * 更新用户信息
 */
export function updateProfile(data: {
  nickname?: string
  avatar?: string
  bio?: string
  school?: string
}): Promise<{ success: boolean }> {
  return request.put('/user/profile', data)
}

/**
 * 修改密码
 */
export interface ChangePasswordRequest {
  old_password: string
  new_password: string
  captcha_id: string
  captcha_code: number
}

export function changePassword(data: ChangePasswordRequest): Promise<{ user: User }> {
  return request.post('/user/change-password', data)
}

export interface RefreshTokenRequest {
  refresh_token?: string
}

export interface RefreshTokenResponse {
  access_token: string
  refresh_token?: string
}

/**
 * 刷新访问令牌
 * Web 端：refresh_token 会自动从 HttpOnly Cookie 中读取
 * App/小程序端：需要传入 refresh_token
 */
export function refreshToken(data?: RefreshTokenRequest): Promise<RefreshTokenResponse> {
  return request.post('/auth/refresh', data || {})
}

/**
 * 重置密码
 */
export interface ResetPasswordRequest {
  email: string
  code: string
  password: string
}

export interface ResetPasswordResponse {
  success: boolean
}

export function resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  return request.post('/auth/reset-password', data)
}

/**
 * 获取用户详情
 */
export function getUserDetail(userId: string): Promise<User> {
  return request.get(`/users/${userId}`)
}

/**
 * 获取登录历史列表
 */
export function getLoginHistoryList(data?: GetLoginHistoryListRequest): Promise<GetLoginHistoryListResponse> {
  return request.get('/user/login-history', data)
}
